import { NavigationRoutes } from 'common/constants';
import FlexibleColumnStorage from 'common/repositories/flexible-column-storage';
import { IColumn } from 'components/elements/table-component/table-component';
import invariant from 'invariant';
import React from 'react';

export type FlexibleColumnsContextType<T = any> = {
  columns: IColumn<T>[];
  setColumns: React.Dispatch<React.SetStateAction<IColumn<T>[]>>;
  moveItem: (fromIndex: number, toIndex: number) => void;
  setColumnHidden: (column: IColumn<any>, hidden: any) => void;
};

const FlexibleColumnsContext = React.createContext<FlexibleColumnsContextType>({
  columns: [],
  setColumns: () => {},
  moveItem: (fromIndex, toIndex) => {},
  setColumnHidden(column, hidden) {},
});

interface FlexibleColumnsProviderProps<T> {
  children: (columns: IColumn<T>[]) => React.ReactNode;
  columns: IColumn<T>[];
  tableName: NavigationRoutes;
}

export default function FlexibleColumnsProvider<T>(
  props: FlexibleColumnsProviderProps<T>,
) {
  const { children, tableName } = props;
  const [columns, setColumns] = React.useState<IColumn<T>[]>(props.columns);

  const moveItem = React.useCallback(
    (fromIndex: number, toIndex: number) => {
      setColumns((prevItems) => {
        const updatedItems = [...prevItems];
        const [movedItem] = updatedItems.splice(fromIndex, 1);
        updatedItems.splice(toIndex, 0, movedItem);
        // set to storage
        FlexibleColumnStorage.set(tableName, updatedItems);
        return updatedItems;
      });
    },
    [tableName],
  );

  const setColumnHidden = React.useCallback(
    (column: IColumn<any>, hidden) => {
      setColumns((prev) => {
        const data = prev.map((item) => {
          const key = `${(item as any).accessorKey}-${item.header}`;
          const currentKey = `${(column as any).accessorKey}-${column.header}`;
          if (key === currentKey) {
            return {
              ...item,
              isHidden: hidden,
            };
          }
          return item;
        });
        FlexibleColumnStorage.set(tableName, data);

        return data;
      });
    },
    [tableName],
  );

  React.useEffect(() => {
    const oldData = FlexibleColumnStorage.get(tableName);

    setColumns(() => {
      // recovery data
      const data = props.columns.map((item: any) => {
        const oldItem = oldData.find(
          (oldItem: any) =>
            `${oldItem.accessorKey}-${oldItem.header}` ===
            `${item.accessorKey}-${item.header}`,
        );

        if (oldItem) {
          return {
            ...item,
            isHidden: oldItem.isHidden,
          };
        }
        return item;
      }) as IColumn<T>[];

      const oldColumns = oldData.map((item: any) => {
        return `${item.accessorKey}-${item.header}`;
      });

      // sort
      data.sort((a: any, b: any) => {
        const keyA = `${a.accessorKey}-${a.header}`;
        const keyB = `${b.accessorKey}-${b.header}`;
        return oldColumns.indexOf(keyA) - oldColumns.indexOf(keyB);
      });

      return data;
    });
  }, [props.columns, tableName]);

  return (
    <FlexibleColumnsContext.Provider
      value={{ columns, setColumns, moveItem, setColumnHidden }}
    >
      {children(columns)}
    </FlexibleColumnsContext.Provider>
  );
}

export function useFlexibleColumns() {
  const context = React.useContext(FlexibleColumnsContext);
  invariant(
    context !== undefined,
    'useFlexibleColumns must be used inside FlexibleColumnsContext',
  );
  return context;
}
